import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type ServiceTypeFormProps = {
  serviceTypeId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const initialValues = {
  name: '',
  code: '',
  description: '',
  active: true,
  parent_id: '' as string | number,
}

export function ServiceTypeForm({ serviceTypeId, onSuccess, onCancel }: ServiceTypeFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!serviceTypeId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [parentOptions, setParentOptions] = useState<Array<{ value: string; label: string }>>([])

  const loadParentOptions = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/service-catalog/service-types', {
        params: { per_page: 500, trash: 0 },
      })
      const list = Array.isArray(data?.data) ? data.data : []
      const parents = list.filter(
        (t: { id?: number; parent_id?: number | null }) =>
          t.parent_id == null && (!serviceTypeId || t.id !== serviceTypeId)
      )
      setParentOptions([
        { value: '', label: '— None (top-level category) —' },
        ...parents.map((t: { id: number; name: string }) => ({ value: String(t.id), label: t.name })),
      ])
    } catch {
      setParentOptions([{ value: '', label: '— None —' }])
    }
  }, [serviceTypeId])

  const loadOne = useCallback(async () => {
    if (!serviceTypeId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/service-catalog/service-types/${serviceTypeId}`)
      const r = data?.data ?? data
      setForm({
        name: r.name ?? '',
        code: r.code ?? '',
        description: r.description ?? '',
        active: r.active !== false,
        parent_id: r.parent_id ?? '',
      })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [serviceTypeId, showError])

  useEffect(() => {
    loadParentOptions()
  }, [loadParentOptions])

  useEffect(() => {
    if (serviceTypeId) {
      loadOne()
    } else {
      setForm(initialValues)
    }
  }, [serviceTypeId, loadOne])

  const updateField = (field: keyof typeof form, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        description: form.description.trim() || null,
        active: form.active,
        parent_id: form.parent_id === '' ? null : Number(form.parent_id),
      }
      if (serviceTypeId) {
        await apiClient.put(`/service-catalog/service-types/${serviceTypeId}`, payload)
        showSuccess('Success', 'Service type updated successfully')
      } else {
        await apiClient.post('/service-catalog/service-types', payload)
        showSuccess('Success', 'Service type created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box py={8}>
        <Text>Loading...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name?.length} isRequired>
            <Input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g. Hardware Support"
            />
            <FormErrorMessage>{errors.name?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Code <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.code?.length} isRequired>
            <Input
              value={form.code}
              onChange={(e) => updateField('code', e.target.value)}
              placeholder="e.g. HW-SUPPORT"
              isDisabled={!!serviceTypeId}
            />
            {!!serviceTypeId && (
              <FormHelperText>Code cannot be changed after creation.</FormHelperText>
            )}
            <FormErrorMessage>{errors.code?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>
          <FormControl isInvalid={!!errors.description?.length}>
            <Textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
            <FormErrorMessage>{errors.description?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Parent (category)
          </FormLabel>
          <FormControl isInvalid={!!errors.parent_id?.length}>
            <Select
              value={form.parent_id === null || form.parent_id === '' ? '' : String(form.parent_id)}
              onChange={(e) => updateField('parent_id', e.target.value === '' ? '' : e.target.value)}
              placeholder={parentOptions.length <= 1 ? 'None' : 'Select parent category'}
            >
              {parentOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <FormHelperText>Leave as None for a top-level service type.</FormHelperText>
            <FormErrorMessage>{errors.parent_id?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>
            Active
          </FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
              colorScheme="blue"
            >
              <Text as="span">Enable this service type for use.</Text>
            </Checkbox>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={saving}
            loadingText={serviceTypeId ? 'Saving...' : 'Creating...'}
          >
            {serviceTypeId ? 'Save' : 'Create Service Type'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
